import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Calendar as CalendarIcon, Copy, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "@/components/SignatureCanvas";

const TYPES = [
  { value: "commande", label: "Commande" },
  { value: "sav", label: "SAV" },
  { value: "fournisseur", label: "Fournisseur" },
  { value: "atelier", label: "Atelier" },
  { value: "custom", label: "Autre" },
];

const emptySlot = () => ({
  start_time: "07:00",
  end_time: "12:00",
  entry_type: "commande",
  reference_id: "",
  custom_label: "",
  comment: "",
  _order_search: "",
});

const slotHours = (s) => {
  if (!s?.start_time || !s?.end_time) return 0;
  const [h1, m1] = s.start_time.split(":").map(Number);
  const [h2, m2] = s.end_time.split(":").map(Number);
  return Math.max(0, (h2 + m2 / 60) - (h1 + m1 / 60));
};

const datesInRange = (start, end) => {
  if (!start || !end) return [];
  const out = [];
  const d = new Date(start);
  const e = new Date(end);
  while (d <= e) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

export const HoursBatchEntryDialog = ({ open, onOpenChange, employee, onSaved, submitAsSubmission = false, prefill = null }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [slots, setSlots] = useState([emptySlot()]);
  const [orders, setOrders] = useState([]);
  const [savs, setSavs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Si prefill fourni (depuis QR code), pré-remplir dates + 1er slot
    if (prefill) {
      setStartDate(prefill.startDate || today);
      setEndDate(prefill.endDate || today);
      const initialSlot = emptySlot();
      if (prefill.orderId) {
        initialSlot.entry_type = "commande";
        initialSlot.reference_id = prefill.orderId;
      }
      setSlots([initialSlot]);
    } else {
      setStartDate(today);
      setEndDate(today);
      setSlots([emptySlot()]);
    }
    setIncludeWeekends(false);
    setSignature(null);
    // Précharger les références pour les sélecteurs commande / SAV / fournisseur
    (async () => {
      try {
        const [ordRes, savRes, supRes] = await Promise.all([
          api.getOrders({ include_hidden: false }),
          api.getSAVs?.() ?? Promise.resolve({ data: [] }),
          api.getSuppliers?.() ?? Promise.resolve({ data: [] }),
        ]);
        // Commandes non annulées (toutes les autres : encaissées incluses pour pouvoir pointer en SAV après)
        setOrders((ordRes.data || []).filter((o) => o.status !== "cancelled"));
        // SAV : on garde ceux qui ne sont PAS terminés/annulés
        setSavs((savRes.data || []).filter((s) => !["termine", "annule"].includes(s.status)));
        setSuppliers(supRes.data || []);
      } catch {
        /* non bloquant */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const targetDates = useMemo(() => {
    const all = datesInRange(startDate, endDate);
    if (includeWeekends) return all;
    return all.filter((d) => {
      const wd = new Date(d).getDay();
      return wd !== 0 && wd !== 6;
    });
  }, [startDate, endDate, includeWeekends]);

  const totalHoursPerDay = slots.reduce((s, sl) => s + slotHours(sl), 0);
  const totalHoursGlobal = totalHoursPerDay * targetDates.length;
  const entriesCount = slots.length * targetDates.length;

  const addSlot = () => setSlots((s) => [...s, emptySlot()]);
  const removeSlot = (idx) => setSlots((s) => s.filter((_, i) => i !== idx));
  const setSlot = (idx, patch) => setSlots((s) => s.map((sl, i) => (i === idx ? { ...sl, ...patch } : sl)));
  const duplicateSlot = (idx) => setSlots((s) => {
    const copy = [...s];
    copy.splice(idx + 1, 0, { ...s[idx] });
    return copy;
  });

  const handleSubmit = async () => {
    if (targetDates.length === 0) {
      toast.error("Aucune date sélectionnée");
      return;
    }
    // Validations
    for (const [i, sl] of slots.entries()) {
      if (!sl.start_time || !sl.end_time || slotHours(sl) <= 0) {
        toast.error(`Tranche ${i + 1} : horaires invalides`);
        return;
      }
      if ((sl.entry_type === "commande" || sl.entry_type === "sav" || sl.entry_type === "fournisseur") && !sl.reference_id) {
        const labels = { commande: "commande", sav: "SAV", fournisseur: "fournisseur" };
        toast.error(`Tranche ${i + 1} : sélectionnez ${sl.entry_type === "fournisseur" ? "un" : "la"} ${labels[sl.entry_type]}`);
        return;
      }
    }
    // Construire le payload batch
    const entries = [];
    for (const d of targetDates) {
      for (const sl of slots) {
        entries.push({
          employee_id: employee.id,
          date: d,
          start_time: sl.start_time,
          end_time: sl.end_time,
          entry_type: sl.entry_type,
          reference_id: ["commande", "sav", "fournisseur"].includes(sl.entry_type) ? sl.reference_id : null,
          custom_label: sl.entry_type === "custom" ? (sl.custom_label || null) : null,
          comment: (sl.comment || "").trim() || null,
        });
      }
    }
    try {
      setSaving(true);
      if (submitAsSubmission) {
        if (!signature) {
          toast.error("Veuillez signer votre fiche avant de la soumettre");
          setSaving(false);
          return;
        }
        await api.submitHoursRequest({
          employee_id: employee.id,
          entries,
          signature,
          signed_at: new Date().toISOString(),
        });
        toast.success(`Demande de ${entries.length} entrée(s) soumise pour validation`);
      } else {
        const { data } = await api.createWorkEntriesBatch({ entries });
        toast.success(`${data.created} entrée(s) enregistrée(s)`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[88vh] overflow-y-auto" data-testid="batch-entry-dialog">
        <DialogHeader>
          <DialogTitle>Saisie d'heures — {employee.first_name} {employee.last_name}</DialogTitle>
          <DialogDescription>
            Saisissez plusieurs jours et plusieurs tâches en même temps. Les entrées seront synchronisées automatiquement avec le Suivi des chantiers.
          </DialogDescription>
        </DialogHeader>

        {/* Plage de dates */}
        <div className="rounded-lg border bg-slate-50 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-slate-600" />
            <Label className="text-sm font-medium">Période</Label>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 w-[160px]" data-testid="batch-start-date" />
            <span className="text-xs text-muted-foreground">au</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 w-[160px]" data-testid="batch-end-date" />
            <label className="flex items-center gap-2 text-xs ml-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeWeekends}
                onChange={(e) => setIncludeWeekends(e.target.checked)}
                data-testid="batch-weekends"
              />
              Inclure weekends
            </label>
            <div className="flex-1" />
            <Badge variant="outline" className="text-xs">{targetDates.length} jour(s)</Badge>
          </div>
        </div>

        {/* Tranches horaires */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Tranches horaires (appliquées chaque jour)</Label>
            <Button size="sm" variant="outline" onClick={addSlot} data-testid="add-slot-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />Ajouter une tranche
            </Button>
          </div>
          <div className="space-y-2">
            {slots.map((sl, i) => (
              <div key={i} className="border rounded-md p-3 bg-white space-y-2" data-testid={`slot-${i}`}>
                <div className="flex items-end gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Début</Label>
                    <Input type="time" value={sl.start_time} onChange={(e) => setSlot(i, { start_time: e.target.value })} className="h-8 w-[110px]" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Fin</Label>
                    <Input type="time" value={sl.end_time} onChange={(e) => setSlot(i, { end_time: e.target.value })} className="h-8 w-[110px]" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px] text-muted-foreground">Type</Label>
                    <Select value={sl.entry_type} onValueChange={(v) => setSlot(i, { entry_type: v, reference_id: "" })}>
                      <SelectTrigger className="h-8" data-testid={`slot-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Badge variant="outline" className="h-8 px-2 font-mono text-xs">{slotHours(sl).toFixed(1)}h</Badge>
                  <Button size="sm" variant="ghost" onClick={() => duplicateSlot(i)} title="Dupliquer">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeSlot(i)} disabled={slots.length <= 1}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>

                {/* Référence selon le type */}
                {sl.entry_type === "commande" && (() => {
                  const q = (sl._order_search || "").trim().toLowerCase();
                  const selected = orders.find((o) => o.id === sl.reference_id);
                  // Si une commande est sélectionnée et que la recherche est vide, on affiche le badge
                  if (selected && !q) {
                    return (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[400px]" data-testid={`slot-order-selected-${i}`}>
                          {selected.client_name}{selected.city ? ` — ${selected.city}` : ""}{selected.order_type_name ? ` — ${selected.order_type_name}` : ""}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => setSlot(i, { reference_id: "", _order_search: "" })}
                        >Changer</Button>
                      </div>
                    );
                  }
                  const filtered = q
                    ? orders.filter((o) => {
                        const ref = (o.reference || "").toLowerCase();
                        const client = (o.client_name || "").toLowerCase();
                        const city = (o.city || "").toLowerCase();
                        const typeName = (o.order_type_name || "").toLowerCase();
                        return ref.includes(q) || client.includes(q) || city.includes(q) || typeName.includes(q);
                      }).slice(0, 20)
                    : [];
                  return (
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Tapez un client, une ville, un n° ou un type…"
                          value={sl._order_search}
                          onChange={(e) => setSlot(i, { _order_search: e.target.value, reference_id: "" })}
                          className="h-8 pl-8"
                          data-testid={`slot-order-search-${i}`}
                          autoComplete="off"
                        />
                      </div>
                      {q && filtered.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                          {filtered.map((o) => (
                            <button
                              key={o.id}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b last:border-b-0"
                              onClick={() => setSlot(i, { reference_id: o.id, _order_search: "" })}
                              data-testid={`slot-order-option-${i}-${o.id}`}
                            >
                              <span className="font-medium">{o.client_name}</span>
                              {o.city && <span className="text-muted-foreground"> — {o.city}</span>}
                              {o.order_type_name && <span className="text-indigo-700"> — {o.order_type_name}</span>}
                              <div className="font-mono text-[10px] text-muted-foreground">{o.reference}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {q && filtered.length === 0 && (
                        <div className="absolute z-10 mt-1 w-full px-3 py-2 text-xs text-muted-foreground bg-white border rounded-md shadow-lg">
                          Aucune commande correspondante.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {sl.entry_type === "sav" && (
                  <Select value={sl.reference_id} onValueChange={(v) => setSlot(i, { reference_id: v })}>
                    <SelectTrigger className="h-8" data-testid={`slot-sav-${i}`}>
                      <SelectValue placeholder={savs.length ? "— Choisir un SAV en cours —" : "Aucun SAV en cours"} />
                    </SelectTrigger>
                    <SelectContent>
                      {savs.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          SAV {s.client_name || s.order_reference || s.id.slice(0, 8)}
                          {s.intervention_date ? ` — ${s.intervention_date}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {sl.entry_type === "fournisseur" && (
                  <Select value={sl.reference_id} onValueChange={(v) => setSlot(i, { reference_id: v })}>
                    <SelectTrigger className="h-8" data-testid={`slot-supplier-${i}`}>
                      <SelectValue placeholder={suppliers.length ? "— Choisir un fournisseur —" : "Aucun fournisseur"} />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name || s.company_name || s.id.slice(0, 8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {sl.entry_type === "custom" && (
                  <Input
                    placeholder="Description libre (ex: Réunion chantier)"
                    value={sl.custom_label}
                    onChange={(e) => setSlot(i, { custom_label: e.target.value })}
                    className="h-8"
                  />
                )}

                {/* Commentaire (toutes les tranches) */}
                <div className="relative">
                  <MessageSquare className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Commentaire (ex: pose finition réalisée, remplacement attendu…)"
                    value={sl.comment || ""}
                    onChange={(e) => setSlot(i, { comment: e.target.value })}
                    className="h-8 pl-7 text-xs"
                    data-testid={`slot-comment-${i}`}
                    maxLength={500}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Résumé */}
        <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 text-sm" data-testid="batch-summary">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-indigo-900">
                {entriesCount} entrée(s) seront créées
              </div>
              <div className="text-xs text-indigo-700 mt-0.5">
                {targetDates.length} jour(s) × {slots.length} tranche(s) — {totalHoursPerDay.toFixed(1)}h / jour
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-indigo-700">Total</div>
              <div className="text-xl font-bold font-mono text-indigo-900">{totalHoursGlobal.toFixed(1)}h</div>
            </div>
          </div>
        </div>

        {submitAsSubmission && (
          <div className="rounded-lg border bg-indigo-50/40 p-3">
            <SignatureCanvas
              value={signature}
              onChange={setSignature}
              label={`Signature de ${employee.first_name} ${employee.last_name}`}
              height={120}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving || entriesCount === 0} data-testid="save-batch-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Enregistrer ${entriesCount} entrée(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HoursBatchEntryDialog;
